import { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAPI, useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { Button, useToast } from "@humansignal/ui";
import { ProjectContext } from "../../providers/ProjectProvider";
import { cn } from "../../utils/bem";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./ReviewQueue.prefix.css";

const block = cn("review-queue");

const REASON_LABEL = {
  low_agreement: "Low agreement",
  outlier: "Dissenting annotator",
  evaluation_fail: "Failed evaluation",
  manual: "Manual flag",
};

const STATUS_LABEL = {
  open: "Open",
  resolved: "Resolved",
  dismissed: "Dismissed",
};

const fmtRel = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Math.round((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

/** A small mark for the row's state: a ring for open, a tick when
 * resolved, a cross when dismissed. */
const Mark = ({ status }) => {
  const kind = status === "resolved" ? "done" : status === "dismissed" ? "off" : "open";
  return (
    <span
      className={block
        .elem("mark")
        .mod({ [kind]: true })
        .toClassName()}
      aria-hidden
    >
      {kind === "done" && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M2 5.2l2.2 2.2L8 3" />
        </svg>
      )}
      {kind === "off" && (
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path d="M2.5 2.5l5 5M7.5 2.5l-5 5" />
        </svg>
      )}
    </span>
  );
};

// Body of the review queue — the tabs / toolbar / list themselves,
// with no outer page chrome. Exported so the DM's Analytics modal can
// render the same UI as the Settings → Review Queue page without
// duplicating the fetch / paging / actions code.
export const ReviewQueueBody = ({ project }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const toast = useToast();
  const history = useHistory();

  const [statusFilter, setStatusFilter] = useState("open");
  const [reasonFilter, setReasonFilter] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actingOn, setActingOn] = useState(null);

  const load = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    const params = { pk: project.id, status: statusFilter, page, page_size: 25 };
    if (reasonFilter) params.reason = reasonFilter;
    const res = await api.callApi("projectReviewQueue", { params, suppressError: true });
    setLoading(false);
    if (res && !res.error) setData(res);
  }, [api, project?.id, statusFilter, reasonFilter, page]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    setPage(1); /* eslint-disable-line */
  }, [statusFilter, reasonFilter]);

  const act = useCallback(
    async (itemId, action) => {
      setActingOn(itemId);
      const res = await api.callApi("projectReviewQueueAction", {
        params: { pk: project.id, itemId },
        body: { action },
        suppressError: true,
      });
      setActingOn(null);
      if (res && !res.error) {
        toast.show({ message: action === "resolve" ? "Resolved" : "Dismissed" });
        load();
      } else {
        toast.show({ message: res?.error ?? "Action failed", type: "error" });
      }
    },
    [api, project?.id, toast, load],
  );

  const rows = data?.results ?? [];
  const total = data?.count ?? 0;
  const counts = data?.status_counts ?? { open: 0, resolved: 0, dismissed: 0 };

  return (
    <div className={block.toClassName()}>
      {/* Filters instead of tabs (owner-requested): status + reason
          sit together in one toolbar, with live counts on the status
          options so nothing was lost from the old tab labels. */}
      <div className={block.elem("toolbar").toClassName()}>
        <select
          className={block.elem("select").toClassName()}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filter by status"
        >
          <option value="open">{`Open, ${counts.open ?? 0}`}</option>
          <option value="resolved">{`Resolved, ${counts.resolved ?? 0}`}</option>
          <option value="dismissed">{`Dismissed, ${counts.dismissed ?? 0}`}</option>
          <option value="all">All statuses</option>
        </select>
        <select
          className={block.elem("select").toClassName()}
          value={reasonFilter}
          onChange={(e) => setReasonFilter(e.target.value)}
          aria-label="Filter by reason"
        >
          <option value="">{t("pages.settings.review_queue.all_reasons")}</option>
          {Object.entries(REASON_LABEL).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
        <span className={block.elem("count").toClassName()}>
          {loading ? "Loading…" : `${total.toLocaleString()} item${total === 1 ? "" : "s"}`}
        </span>
      </div>

      <div className={block.elem("list").toClassName()}>
        {rows.length === 0 && !loading && (
          <div className={block.elem("empty").toClassName()}>
            Nothing in the queue. Either nothing has tripped the automated flags yet, or every flag has been resolved.
          </div>
        )}
        {rows.map((row) => {
          const score =
            row.reason_score == null
              ? ""
              : row.reason === "low_agreement"
                ? `agreement ${(row.reason_score * 100).toFixed(0)}%`
                : row.reason === "outlier"
                  ? `personal mean ${(row.reason_score * 100).toFixed(0)}%`
                  : `score ${row.reason_score.toFixed(3)}`;
          const shift = row.metadata?.drift ? row.metadata.shifts?.[0] : null;
          // State first, then the reason and its figures, then who and when.
          const note = [
            row.status === "open"
              ? STATUS_LABEL.open
              : `${STATUS_LABEL[row.status]}${row.reviewer_email ? ` by ${row.reviewer_email}` : ""}`,
            score,
            row.metadata?.annotator_email ? `annotator ${row.metadata.annotator_email}` : "",
            shift ? `drift on ${shift.label}, ${shift.baseline.toFixed(2)} to ${shift.recent.toFixed(2)}` : "",
            fmtRel(row.created_at),
          ]
            .filter(Boolean)
            .join(", ");
          return (
            <article key={row.id} className={block.elem("row").toClassName()}>
              <Mark status={row.status} />
              <div className={block.elem("main").toClassName()}>
                <div className={block.elem("name").toClassName()}>
                  <button
                    type="button"
                    className={block.elem("task-link").toClassName()}
                    onClick={() => history.push(`/projects/${project.id}/data?task=${row.task_id}`)}
                  >
                    Task {row.task_id}
                  </button>
                  <span className={block.elem("reason").toClassName()}>{REASON_LABEL[row.reason] ?? row.reason}</span>
                </div>
                <div className={block.elem("sub").toClassName()}>{note}.</div>
              </div>
              {row.status === "open" && (
                <div className={block.elem("actions").toClassName()}>
                  <Button
                    type="button"
                    look="outlined"
                    size="small"
                    style={{ width: 96 }}
                    onClick={() => act(row.id, "resolve")}
                    disabled={actingOn === row.id}
                  >
                    Resolve
                  </Button>
                  <Button
                    type="button"
                    variant="neutral"
                    look="outlined"
                    size="small"
                    style={{ width: 96 }}
                    onClick={() => act(row.id, "dismiss")}
                    disabled={actingOn === row.id}
                  >
                    Dismiss
                  </Button>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
};

// Page wrapper — adds the SettingsPageHeader + tab-body class so the
// existing Settings route continues to look right. The modal consumer
// pulls in ``ReviewQueueBody`` directly and provides its own chrome.
export const ReviewQueue = () => {
  const { t } = useTranslation();
  const { project } = useContext(ProjectContext);
  useUpdatePageTitle(createTitleFromSegments([project?.title, "Review Queue"]));
  return (
    <div className={cn("settings-page").mod({ wide: true }).toClassName()}>
      <SettingsPageHeader
        title={t("pages.settings.review_queue.review_queue")}
        subtitle={t("pages.settings.review_queue.tasks_flagged_by_the_automated_quality_c")}
      />
      <ReviewQueueBody project={project} />
    </div>
  );
};

ReviewQueue.title = "Review queue";
ReviewQueue.path = "/review";
ReviewQueue.exact = true;
